import React from 'react';
import { useIntl } from 'react-intl';

import { useAppSelector } from '../../../../store/hooks';
import { AppointmentEvent, Customer } from '../../../../types';
import { SeriesContextSection } from './SeriesContextSection';

import styles from './CustomerSearchSection.module.scss';

type SelectedCustomerCardProps = {
  seriesId: string;
  isFormDirty: boolean;
  onChange: () => void;
};

function buildFullName(customer: Customer): string {
  return [customer.name, customer.lastName].filter(Boolean).join(' ').trim();
}

function buildContactLine(customer: Customer): string {
  return [customer.email, customer.phoneNumber].filter(Boolean).join(' · ');
}

function SelectedCustomerCardComponent({ seriesId, isFormDirty, onChange }: SelectedCustomerCardProps) {
  const intl = useIntl();
  const seriesState = useAppSelector((state) => state.series.getSeriesById);

  // Customer is sourced from the series' root event (or the first event as
  // fallback). The fetch is triggered by SeriesContextSection below; both
  // components subscribe to the same slice.
  const seriesData = seriesState.data;
  let customer: Customer | null = null;
  if (seriesData) {
    const { series, events } = seriesData;
    const rootEvent = series.rootEventId
      ? events.find((e) => e.id === series.rootEventId)
      : undefined;
    const sourceEvent = rootEvent ?? events[0];
    customer = (sourceEvent?.eventTypeData as AppointmentEvent | undefined)?.customer ?? null;
  }

  return (
    <div className={styles.selectedCard} data-testid="series-selected-customer-card">
      <div className={styles.selectedCardHeader}>
        <div>
          {customer && (
            <>
              <div className={styles.selectedCardName}>{buildFullName(customer)}</div>
              {buildContactLine(customer) && (
                <div className={styles.selectedCardContact}>{buildContactLine(customer)}</div>
              )}
            </>
          )}
        </div>
        <button
          type="button"
          className={styles.selectedCardChangeButton}
          data-testid="series-selected-customer-change"
          onClick={onChange}
        >
          {intl.formatMessage({ id: 'seriesbooking.common.change' })}
        </button>
      </div>

      <div className={styles.selectedCardDivider} />
      <SeriesContextSection seriesId={seriesId} isFormDirty={isFormDirty} />
    </div>
  );
}

export const SelectedCustomerCard = React.memo(SelectedCustomerCardComponent);
